import type { ActivityLogicPlugin } from '../../core/types/plugin.base.types';
import type { AnatomiaPayload, AnatomiaRuntime } from '../../core/types/payloads/anatomia.types';
import type { RhythmicCell } from '../../core/music/rhythmicCellEngine';
import type { MusicalToken } from '../../core/types/music';
import type { RoutingMode } from '../../core/types/room.types';
import type { HostActionPayload, StudentInputEnvelope } from '../../core/types/events.types';
import {
  isMusicalToken,
  normalizeTokenArray,
  tokenBeats,
  tokenToLabel,
  tokenToSymbol,
} from '../../core/music/tokens';

export function validateAnatomiaPayload(raw: unknown): AnatomiaPayload {
  if (!raw || typeof raw !== 'object') throw new Error('Anatomia payload must be an object');
  const data = raw as Partial<AnatomiaPayload>;
  if (!data.title) throw new Error('Anatomia requires title');
  if (!Array.isArray(data.items) || data.items.length < 1) {
    throw new Error('Anatomia requires items');
  }

  const rhythmMethod =
    data.rhythmMethod === 'takadimi' || data.rhythmMethod === 'kodaly'
      ? data.rhythmMethod
      : 'tradicional';

  const items = data.items.map((item, i) => {
    const tokenRaw = item.token ?? item.symbol ?? item.figureLabel;
    const token: MusicalToken = isMusicalToken(String(tokenRaw))
      ? (String(tokenRaw) as MusicalToken)
      : normalizeTokenArray([tokenRaw, item.figureLabel])[0] ?? 'Q';
    return {
      id: String(item.id || `a${i + 1}`),
      token,
      symbol: tokenToSymbol(token),
      figureLabel: item.figureLabel ? String(item.figureLabel) : tokenToLabel(token, rhythmMethod),
      correctBeats: Number(item.correctBeats) || tokenBeats(token),
      anatomyPrompt: item.anatomyPrompt ? String(item.anatomyPrompt) : undefined,
      cellId: item.cellId ? String(item.cellId) : undefined,
      takadimi: item.takadimi ? String(item.takadimi) : undefined,
      isRest: item.isRest === true,
      modifiers: item.modifiers,
    };
  });

  return {
    title: String(data.title),
    meter: data.meter ? String(data.meter) : '4/4',
    rhythmMethod,
    items,
    targetBarNumber:
      typeof data.targetBarNumber === 'number' && data.targetBarNumber >= 1
        ? data.targetBarNumber
        : undefined,
    beatCells: Array.isArray(data.beatCells) ? (data.beatCells as RhythmicCell[]) : undefined,
    rhythmicCells: Array.isArray(data.rhythmicCells)
      ? (data.rhythmicCells as RhythmicCell[])
      : undefined,
  };
}

export function createInitialAnatomiaRuntime(_payload: AnatomiaPayload, _routing: RoutingMode): AnatomiaRuntime {
  return { responses: {}, finalized: {}, aggregates: { submitted: 0, correct: 0 } };
}

function scoreAnatomia(payload: AnatomiaPayload, answers: Record<string, number | string>): number {
  let correct = 0;
  for (const item of payload.items) {
    const raw = answers[item.id];
    const num = typeof raw === 'number' ? raw : Number(String(raw).replace(',', '.'));
    if (!Number.isNaN(num) && num === item.correctBeats) correct += 1;
  }
  return correct;
}

export const anatomiaLogic: ActivityLogicPlugin<'anatomia'> = {
  mode: 'anatomia',
  validatePayload: validateAnatomiaPayload,
  createInitialRuntime: createInitialAnatomiaRuntime,

  onStudentInput(ctx, input: StudentInputEnvelope, participant) {
    if (input.action === 'FILL') {
      const data = input.data as { itemId: string; value: string | number };
      const prev = ctx.runtime.responses[participant.participantId] ?? {};
      const responses = {
        ...ctx.runtime.responses,
        [participant.participantId]: { ...prev, [data.itemId]: data.value },
      };
      return { runtime: { ...ctx.runtime, responses } };
    }
    if (input.action === 'SUBMIT') {
      const answers = ctx.runtime.responses[participant.participantId] ?? {};
      const correct = scoreAnatomia(ctx.payload, answers);
      const finalized = { ...ctx.runtime.finalized, [participant.participantId]: true };
      const all = Object.keys(finalized).filter((id) => finalized[id]);
      return {
        runtime: {
          ...ctx.runtime,
          finalized,
          aggregates: {
            submitted: all.length,
            correct: all.reduce(
              (sum, pid) => sum + scoreAnatomia(ctx.payload, ctx.runtime.responses[pid] ?? {}),
              0,
            ),
          },
        },
        scores: [{ participantId: participant.participantId, delta: correct, reason: 'anatomia' }],
      };
    }
    return { runtime: ctx.runtime, rejected: true, rejectReason: 'UNKNOWN_ACTION' };
  },

  onHostAction(ctx, _action: HostActionPayload) {
    return { runtime: ctx.runtime };
  },
};

export function demoAnatomiaPayload(): AnatomiaPayload {
  return validateAnatomiaPayload({
    title: 'Duración de figuras rítmicas',
    meter: '4/4',
    items: [
      { id: 'a1', token: 'Q', correctBeats: 1 },
      { id: 'a2', token: 'H', correctBeats: 2 },
      { id: 'a3', token: 'E', correctBeats: 0.5 },
    ],
  });
}
